import React, { useState, useEffect, useRef } from 'react'
import { CheckPicker } from 'rsuite'
import { IFSkeleton, IFText } from 'Components'
import { Colors } from 'Theme'
import { useTranslation } from 'react-i18next'
import Styles from './SearchableCheckPicker.module.css'
import styled from 'styled-components'
import { isPropsMatch } from 'Utils/PropsMatch'

const StyledCheckPicker = styled(CheckPicker)`
  && {
    width: 100%;
    .rs-picker-toggle-active {
      border-color: ${Colors.primary};
    }
  }
`

const SearchableCheckPicker = ({
  data,
  loadMore,
  loading,
  onSearch,
  style,
  onChange,
  value,
  disabled,
  defaultValue,
  setError,
  size,
  placeholder,
  paginationOffset,
  cleanable = true,
  canLoadMore = true,
  searchable = true,
  renderMenuItem: renderMenuItemProp,
  handleClosePicker = () => {},
}) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedValues, setSelectedValues] = useState(
    Array.isArray(value) && value.length
      ? value
      : Array.isArray(defaultValue)
      ? defaultValue
      : [],
  )
  const [isTouched, setIsTouched] = useState(false)
  const [formattedData, setFormattedData] = useState([])
  const [menuOpen, setMenuOpen] = useState(false)
  const pickerRef = useRef()

  useEffect(() => {
    const safeData = Array.isArray(data) ? data : []
    if (loading && searchQuery.length !== 0) {
      setFormattedData([
        { label: 'Skeleton', value: 'Skeleton' },
        { label: 'Skeleton', value: 'Skeleton' },
        { label: 'Skeleton', value: 'Skeleton' },
        { label: 'Skeleton', value: 'Skeleton' },
      ])
    } else if (
      loading &&
      searchQuery.length === 0 &&
      paginationOffset === null
    ) {
      setFormattedData([
        { label: 'Skeleton', value: 'Skeleton' },
        { label: 'Skeleton', value: 'Skeleton' },
        { label: 'Skeleton', value: 'Skeleton' },
        { label: 'Skeleton', value: 'Skeleton' },
      ])
    } else if (loading && searchQuery.length !== 0) {
      setFormattedData(safeData)
    } else if (paginationOffset !== null && searchQuery.length === 0) {
      setFormattedData([
        ...safeData,
        { label: 'Skeleton', value: 'Skeleton' },
        { label: 'Skeleton', value: 'Skeleton' },
      ])
    } else {
      setFormattedData(safeData)
    }
  }, [loading, searchQuery, data, paginationOffset])

  const handleSearch = (q) => {
    setSearchQuery(q)
    onSearch?.(q)
  }

  const handleChange = (selected) => {
    if (Array.isArray(selected) && selected.includes('Skeleton')) return
    setSelectedValues(selected || [])
    onChange?.(selected || [])
    if (selected?.length) setError?.('')
    else if (isTouched) setError?.(t('IFSelect.Required'))
  }

  const handleOpen = () => {
    setMenuOpen(true)
    setIsTouched(true)
  }
  const handleClose = () => {
    handleClosePicker()
    setMenuOpen(false)
    if (isTouched && (!selectedValues || selectedValues.length === 0)) {
      setError?.(t('IFSelect.Required'))
    }
  }

  useEffect(() => {
    const handleScroll = (e) => {
      const menuEl = document.querySelector('.rs-picker-menu')
      if (menuEl && menuEl.contains(e.target)) return
      if (menuOpen && pickerRef?.current?.close) {
        pickerRef.current.close()
        const wrapper = pickerRef.current?.root
        const toggle = wrapper?.querySelector('.rs-picker-toggle')
        if (toggle) {
          toggle.blur()
          toggle.classList.remove('rs-picker-toggle-active')
        }
        setMenuOpen(false)
      }
    }
    window.addEventListener('scroll', handleScroll, true)
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [menuOpen])

  useEffect(() => {
    let observer
    let activeEl
    const disableKeys = (e) => {
      const keysToDisable = ['ArrowDown', 'ArrowUp', 'Enter']
      if (keysToDisable.includes(e.key)) {
        e.preventDefault()
        e.stopPropagation()
      }
    }
    if (menuOpen) {
      const attachListener = () => {
        activeEl = document.activeElement
        if (activeEl) activeEl.addEventListener('keydown', disableKeys)
      }
      const timer = setTimeout(attachListener, 0)
      observer = new MutationObserver(() => {
        if (document.activeElement !== activeEl) {
          if (activeEl) activeEl.removeEventListener('keydown', disableKeys)
          attachListener()
        }
      })
      observer.observe(document.body, { subtree: true, childList: true })

      return () => {
        clearTimeout(timer)
        if (activeEl) activeEl.removeEventListener('keydown', disableKeys)
        observer?.disconnect()
      }
    }
  }, [menuOpen])

  const onItemsRendered = (props) => {
    if (!canLoadMore) return
    const baseLen = Array.isArray(data) ? data.length : 0
    if (props.visibleStopIndex >= baseLen - 1) {
      loadMore?.()
    }
  }

  const renderMenu = (menu) => (
    <div className={Styles.MenuContainer}>{menu}</div>
  )

  const defaultRenderMenuItem = (label, item) => (
    <div>
      {label === 'Skeleton' ? (
        <div className={Styles.Skeleton}>
          <IFSkeleton variant="text" />
        </div>
      ) : (
        <IFText className={Styles.MenuItem} style={{ color: Colors.text }}>
          {label}
        </IFText>
      )}
    </div>
  )

  const renderMenuItem = (label, item) =>
    typeof renderMenuItemProp === 'function'
      ? renderMenuItemProp(label, item)
      : defaultRenderMenuItem(label, item)

  return (
    <div className={Styles.WrapperDiv}>
      <StyledCheckPicker
        placement="autoVertical"
        searchable={searchable}
        ref={pickerRef}
        onClean={() => {
          pickerRef?.current?.close?.()
        }}
        container={() => document.body}
        preventOverflow={false}
        cleanable={cleanable}
        menuMaxHeight={250}
        menuAutoWidth
        size={size}
        data={canLoadMore ? formattedData : Array.isArray(data) ? data : []}
        style={{ ...style }}
        virtualized
        onOpen={handleOpen}
        onClose={handleClose}
        renderMenu={renderMenu}
        disabledItemValues={['Skeleton']}
        renderMenuItem={renderMenuItem}
        listProps={{ onItemsRendered }}
        onSearch={handleSearch}
        searchBy={() => true}
        value={selectedValues}
        onChange={handleChange}
        placeholder={disabled ? defaultValue : placeholder}
        disabled={disabled}
      />
    </div>
  )
}

function shouldSkipRender(prevProps, nextProps) {
  return isPropsMatch(prevProps, nextProps, [
    'data',
    'loadMore',
    'loading',
    'onSearch',
    'style',
    'onChange',
    'value',
    'disabled',
    'defaultValue',
    'setError',
    'size',
    'placeholder',
    'paginationOffset',
    'cleanable',
    'canLoadMore',
    'searchable',
  ])
}

export default React.memo(SearchableCheckPicker, shouldSkipRender)
